/*
 * @providesModule SMSVerify
 */

import React, {useEffect, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {_m} from 'INTL';
import {BannerAnimated, CountryData, formatCountdown, useCountdownNumber} from '@skillz/ui';
import {SkillzButton} from '@skillz-rn/atoms';
import {defaultColor, errorColor, getStyles, greyColor} from 'SMSVerifyStyle';
import SMSVerifyController from 'SMSVerifyController';
import {legacyStyles, createNewUIStyles} from './SMSVerifyStyles';
import {
  ImageStyle,
  LayoutAnimation,
  Platform,
  SafeAreaView,
  Text,
  TextInput,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import WebView from 'react-native-webview';
import {SMSCodeInput} from 'SMSCodeInput';
import {MessageLabel, SMSEvents, SMSPhoneInput} from 'SMSPhoneInput';
import {CountriesList} from 'CountriesList';
import SkillzSVG from 'SkillzSVG';
import SignInMessage from 'SignInMessage';
import SmsMarketingCheckbox from 'SmsMarketingCheckbox';
import CurrentUserStore from '../../Stores/CurrentUser/CurrentUserStore';
import SkillzStyle from 'SkillzStyle';
import PushButton from 'PushButton';
import ClientPropertyConstants from 'ClientPropertyConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';

const CALL_ME_COOL_DOWN = 60; // 60 seconds
const RESEND_COOL_DOWN = 60; // 60 seconds
const SECOND_TO_MS = 1000;
const SKILL_TOS_URL = 'https://skillz.com/legal';
const PHONE_IN_USE_ERROR = 'PHONE_IN_USE';
const DEFAULT_COUNTRY = 'us';

export type SMSProps = {
  /** Message text. Takes header/ body attributes */
  messageText: MessageLabel;
  /** List of countries used for the CountryListModal */
  countryData: CountryData[];
  /** Number of codeInput boxes to display/ length of verification code */
  numberOfInputs: number;
  /** Boolean to whether to show sms marketing checkbox */
  showSmsMarketingCheckbox: boolean;
  /** Boolean to whether to show tos or just data fee label */
  showTosMessage: boolean;
  /** Object that passes state and state setting function to show/hide Tos */
  tosValues?: {
    showTos: boolean;
    setShowTos: React.Dispatch<React.SetStateAction<boolean>>;
  };
  /** Loading functions show/hide*/
  loading?: {
    show: () => void;
    hide: () => void;
  };
  /** Optional container style prop*/
  containerStyle?: ViewStyle;
  /** Strings for event calls */
  events: SMSEvents;
  // Verification code state
  verificationCodeValues: {
    verificationCode: boolean;
    setVerificationCode: React.Dispatch<React.SetStateAction<boolean>>;
  };
  /** Optional boolean to allow sign in from this screen*/
  allowSignIn?: boolean;
  /** Optional field to input the phone number*/
  inputPhoneNumber?: string;
  /** Optional bool indicating the phone number is already in use*/
  phoneNumberInUse?: boolean;
  /** Optional field to input the country*/
  inputCountry?: string;
  /** Optional field to input the country value*/
  inputCountryValue?: string;
  /** Function used to validate input for the phone number input box */
  isValidNumber: (phoneNumber: string, country: string) => boolean;
  /** Function used to request verification code */
  sendSMSVerification: (
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (errorString: string) => void,
    call?: boolean
  ) => void;
  /** function to fetch default country*/
  fetchCountry: (setCountry: React.Dispatch<React.SetStateAction<string>>) => void;
  /**  Function used to format input for the phone number input box*/
  formatNumber: (number: string, iso2: string) => string;
  /** Function used to get country code for a given country */
  getCountryCode: (country: string) => string;
  /**
   *
   * @param country Country used to get flag
   * @param style Passed in to given function to style flag
   * @returns flag image
   */
  getFlag: (country: string, style: ImageStyle) => React.ReactElement;
  /** Function for validating verification code inputted by user */
  codeValidation: (
    code: string,
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (errorString: string) => void
  ) => void;
  //Function to track aws events
  trackEvent: (name: string, params?: Object, metrics?: Object) => void;
  signInPress?: () => void;
  /** Function to redirect user to the login screen */
  onPhoneNumberInUse?: (phoneNumber: string, country: string, countryValue: string) => void;
  // boolean to disable editing of phone number
  disableEdit?: boolean;
  colorVariant?: 'dark';
  isNewUI?: boolean;
  gameTheme?: object;
  userAlreadyHaveAccount?: boolean;
  onClosePress?: () => void;
};

/**
 * This Component is the SMS Verification flow
 * This is used to show the Phone Number Input,
 * Country Code input, and SMS Code input
 * @param {SMSProps}
 */
export const SMSVerify = ({
  messageText,
  countryData,
  numberOfInputs,
  showTosMessage,
  tosValues,
  loading,
  containerStyle,
  events,
  verificationCodeValues,
  allowSignIn,
  inputPhoneNumber,
  phoneNumberInUse,
  inputCountry,
  inputCountryValue,
  isValidNumber,
  fetchCountry,
  sendSMSVerification,
  getCountryCode,
  formatNumber,
  getFlag,
  codeValidation,
  trackEvent,
  signInPress,
  onPhoneNumberInUse,
  colorVariant,
  showSmsMarketingCheckbox,
  disableEdit,
  isNewUI,
  gameTheme,
  userAlreadyHaveAccount,
  onClosePress,
}: SMSProps) => {
  const [callInsteadEnabled, setCallInsteadEnabled] = useState(true);
  const [country, setCountry] = useState(inputCountry || DEFAULT_COUNTRY);
  const [countryValue, setCountryValue] = useState(inputCountryValue || '+1');
  const [errorStyle, setErrorStyle] = useState(false);
  const [showCountryList, setShowCountryList] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(inputPhoneNumber || '');
  const [oldPhoneNumber, setOldPhoneNumber] = useState(inputPhoneNumber || '');
  const {verificationCode, setVerificationCode} = verificationCodeValues;
  const [errorMessage, setErrorMessage] = useState('');
  const [showBanner, setShowBanner] = useState(false);
  const [resendEnabled, setResendEnabled] = useState(true);
  const [resendTimer, setResendTimer] = useState(0); // sets timer for resend code button
  const [callMeTimer, setCallMeTimer] = useState(0); // sets timer for call me instead button
  const styles = getStyles(colorVariant);
  const {t} = useTranslation();
  const [isSmsOptInAccepted, setIsSmsOptInAccepted] = useState(true);
  const [isSmsChecked, setIsSmsChecked] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const [currentOTP, setCurrentOTP] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResendCodeEnabled, setIsResendCodeEnabled] = useState(false);
  const otpDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);

  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;

  const newUIStyles = createNewUIStyles(gameTheme, isDarkTheme);

  const parsedNumber = phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '');

  const fullNumber = `${countryValue}${parsedNumber}`;

  const _codeValidation = (
    code: string,
    phone: string,
    onSuccess: () => void,
    onFailure: (error: string) => void
  ) => {
    if (otpDebounceRef.current) {
      clearTimeout(otpDebounceRef.current);
    }

    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
    }

    animationFrameIdRef.current = requestAnimationFrame(() => {
      otpDebounceRef.current = setTimeout(() => {
        setCurrentOTP(code);
      }, 100);
    });
  };

  const callbackTimer = () => {
    setShowBanner(false);
    setResendEnabled(true);
  };

  const handleSmsToggle = (isChecked: boolean) => {
    setIsSmsChecked(isChecked);
  };

  const fetchSMSMarketingStatus = async () => {
    const optInStatus: string | null = await CurrentUserStore.getUserProperty('optInStatus');
    setIsSmsOptInAccepted(optInStatus === 'true');
  };

  useEffect(() => {
    fetchSMSMarketingStatus();
  }, []);

  useEffect(() => {
    return () => {
      if (otpDebounceRef.current) {
        clearTimeout(otpDebounceRef.current);
      }
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (resendEnabled) {
      setResendTimer(0);
    } else {
      setResendTimer(RESEND_COOL_DOWN);
    }
  }, [resendEnabled]);

  useEffect(() => {
    if (callInsteadEnabled) {
      setCallMeTimer(0);
    } else {
      setCallMeTimer(CALL_ME_COOL_DOWN);
    }
  }, [callInsteadEnabled]);

  const resendTime = useCountdownNumber(resendTimer, callbackTimer); //time in seconds(number) for resend timer
  const callTime = useCountdownNumber(callMeTimer, () => setCallInsteadEnabled(true)); //time in seconds(number)for call me instead timer

  useEffect(() => {
    if (country !== DEFAULT_COUNTRY) {
      return;
    }
    fetchCountry(setCountry);
  }, [fetchCountry, setCountry]);

  useEffect(() => {
    const parsedInputNumber = phoneNumber.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
    const matchingCountry = parsedInputNumber?.startsWith('+')
      ? SMSVerifyController.getRegionFromPhoneNumber('+' + parsedNumber)
      : null;

    if (matchingCountry) {
      // Update state with the matched country details
      const currentCountryValue = getCountryCode(matchingCountry);
      let currentPhone = phoneNumber.slice(currentCountryValue.length);
      setCountry(matchingCountry);
      setCountryValue(currentCountryValue);
      setPhoneNumber(formatNumber(currentPhone, country));
    } else {
      const currentCountryValue = getCountryCode(country);
      setCountryValue(currentCountryValue);
      setPhoneNumber(formatNumber(phoneNumber, country));
    }

    setErrorStyle(false);
    setResendEnabled(true); //reset timer if phone number is changed
    setCallInsteadEnabled(true); //reset timer if phone number is changed
  }, [country, phoneNumber, isValidNumber, formatNumber, getCountryCode]);
  /* istanbul ignore next */ // not testing the code input state

  const editPress = () => {
    if (userAlreadyHaveAccount && onClosePress) {
      onClosePress();
      return;
    }
    LayoutAnimation.easeInEaseOut();
    setErrorStyle(false);
    setVerificationCode(false);
  };

  useEffect(() => {
    setErrorStyle(false);
    setShowBanner(false);
  }, [verificationCode]);

  useEffect(() => {
    if (inputPhoneNumber && !verificationCode) {
      onSendPressed();
    }
  }, [inputPhoneNumber]);

  useEffect(() => {
    if (showSmsMarketingCheckbox) return;
    // Skip auto-send for new UI
    // if (isNewUI) {
    //   setOldPhoneNumber(phoneNumber);
    //   return;
    // }
    if (phoneNumber.length - oldPhoneNumber.length > 0) {
      if (parsedNumber.length >= 2 && isValidNumber(fullNumber, country)) {
        onSendPressed();
      }
    }
    setOldPhoneNumber(phoneNumber);
  }, [phoneNumber, fullNumber, isNewUI]);

  const onSendOTPPressed = () => {
    const noSpecialCharacters = phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '');
    if (currentOTP.length === numberOfInputs) {
      setIsLoading(true);
      setIsResendCodeEnabled(true);
      codeValidation(
        currentOTP,
        `${countryValue}${noSpecialCharacters}`,
        () => {
          setIsLoading(false);
        },
        (errorString) => {
          setErrorMessage(errorString);
          setErrorStyle(true);
          setIsLoading(false);
        }
      );
    }
  };

  const onSendPressed = () => {
    if (showSmsMarketingCheckbox) {
      CurrentUserStore.updateUserProperty('optInStatus', isSmsChecked.toString());
    }
    setIsLoading(true);
    LayoutAnimation.easeInEaseOut();
    /* istanbul ignore next */ // not testing phone number logic
    if (verificationCode) {
      const noSpecialCharacters = phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '');
      sendSMSVerification(
        fullNumber,
        () => {
          setIsLoading(false);
        },
        (errorString) => {
          handleSMSValidationError(errorString, noSpecialCharacters, country, countryValue);
          setIsLoading(false);
        }
      );
      trackEvent(events.codeRequest);
      setShowBanner(true);
      setResendEnabled(false);
    } else {
      if (parsedNumber.length >= 2 && !isValidNumber(fullNumber, country)) {
        /* istanbul ignore next */ // not testing the code input state
        setErrorMessage(_m('Invalid phone number'));
        setErrorStyle(true);
        setIsLoading(false);
      } else {
        /* istanbul ignore next */ // not testing the code input state
        sendSMSVerification(
          fullNumber,
          () => {
            setVerificationCode(true);
            setIsLoading(false);
          },
          (errorString) => {
            handleSMSValidationError(errorString, parsedNumber, country, countryValue);
            setIsLoading(false);
          }
        );
        trackEvent(events.codeRequest);
      }
    }
  };

  const renderErrorMessage = (error: string, codeStyle: boolean) => {
    let errorStyle = isNewUI
      ? newUIStyles.errorMessageNewUI
      : codeStyle
        ? styles.errorMessageCode
        : styles.errorMessage;
    return (
      <View style={errorStyle}>
        <SkillzSVG
          source={'icon_info_filled.svg'}
          style={styles.errorInfo}
          tintColor={errorColor}
        />
        <Text
          allowFontScaling={false}
          style={[
            styles.labelPhoneInput,
            {
              color: errorStyle ? errorColor : defaultColor,
              ...styles.errorText,
            },
          ]}
        >
          {error}
        </Text>
      </View>
    );
  };

  const renderSendCodeButton = () => {
    const isResendCodeButton = Boolean(verificationCode || inputPhoneNumber);
    const buttonTextColor = isResendCodeButton ? defaultColor : '#FFF';
    const resendText = {textColor: buttonTextColor};
    const errorBackgroundColor = {backgroundColor: defaultColor};
    const backgroundColor = errorBackgroundColor;
    const backgroundColorDefault = {backgroundColor: greyColor};

    const buttonContainerStyleActive = isResendCodeButton
      ? {}
      : {
          ...backgroundColor,
          ...styles.button,
        };
    const buttonContainerStyleDefault = isResendCodeButton
      ? {}
      : {
          ...backgroundColorDefault,
          ...styles.buttonDefault,
        };

    let text = isResendCodeButton ? _m('Resend Code') : _m('Next');
    text +=
      !resendEnabled && verificationCode
        ? ` in ${formatCountdown(t, resendTime * SECOND_TO_MS)}`
        : '';
    const dataFeeExtraStyle = allowSignIn
      ? legacyStyles.dataFeeStyleBold
      : legacyStyles.dataFeeStyleLight;
    return (
      <View>
        <View>
          {!ClientPropertyConstants.getFTUEV2FeatureEnabled() &&
            !isSmsOptInAccepted &&
            !(verificationCode || inputPhoneNumber) && (
              <SmsMarketingCheckbox
                showCheckbox={showSmsMarketingCheckbox}
                wrapperStyle={isNewUI ? styles.newSmsMarketingWrapper : styles.smsMarketingWrapper}
                onToggle={handleSmsToggle}
                isNewUI={isNewUI}
              />
            )}
        </View>
        {isNewUI ? (
          renderNewUINextButton(text)
        ) : (
          <View style={!isNewUI && {paddingHorizontal: 20}}>
            <SkillzButton
              text={text}
              onPress={onSendPressed}
              size={'L'}
              type={'Tertiary'}
              style={resendText as any}
              containerStyle={
                parsedNumber.length >= 2 && isValidNumber(fullNumber, country)
                  ? buttonContainerStyleActive
                  : buttonContainerStyleDefault
              }
              testID={'SendCode'}
              branding={'Skillz'}
            />
          </View>
        )}
        {(parsedNumber.length >= 2 && !isValidNumber(fullNumber, country)) ||
        (!resendEnabled && verificationCode) ? (
          <TouchableOpacity
            onPressIn={() => {
              if (parsedNumber.length >= 2 && isValidNumber(fullNumber, country)) {
                setErrorStyle(false);
              } else {
                setErrorStyle(true);
                setErrorMessage(_m('Invalid phone number'));
              }
            }}
          >
            <View />
          </TouchableOpacity>
        ) : null}

        <View>
          {!verificationCode && !showTosMessage && !showSmsMarketingCheckbox && (
            <Text allowFontScaling={false} style={[styles.dataFee, dataFeeExtraStyle]}>
              {_m('Messages and data fees may apply.') + ' '}
            </Text>
          )}
          {allowSignIn && signInPress && !isNewUI ? (
            <SignInMessage isNewUI={isNewUI} signInPress={signInPress} />
          ) : null}
        </View>
      </View>
    );
  };

  /* istanbul ignore next */
  const renderTos = () => {
    return (
      <SafeAreaView style={styles.tosView}>
        <WebView source={{uri: SKILL_TOS_URL}} onLoad={loading?.hide} />
      </SafeAreaView>
    );
  };

  /* istanbul ignore next */
  const renderTosMessage = () => {
    const textStyle: TextStyle = {...styles.editButton, fontWeight: '400'};
    return (
      <View>
        <Trans
          t={_m}
          i18nKey={'SMS_TOS_COPY'}
          parent={Text}
          components={{
            tos: (
              <Text
                allowFontScaling={false}
                onPress={() => {
                  if (Platform.OS === 'web') {
                    window.open(SKILL_TOS_URL, '_blank');
                  } else {
                    loading?.show();
                    tosValues?.setShowTos(true);
                  }
                }}
                suppressHighlighting={true}
                style={[textStyle, isNewUI && newUIStyles.tosLinkNewUI]}
              />
            ),
          }}
          style={[styles.tosMessage, isNewUI && newUIStyles.tosMessageNewUI]}
        />
      </View>
    );
  };

  const handleSMSValidationError = (
    errorString: string,
    phoneNumber: string,
    country: string,
    countryValue: string
  ) => {
    if (errorString === PHONE_IN_USE_ERROR && onPhoneNumberInUse) {
      onPhoneNumberInUse?.(phoneNumber, country, countryValue);
    } else {
      if (errorString === PHONE_IN_USE_ERROR) {
        setErrorMessage(_m('This phone number is already in use'));
      } else {
        setErrorMessage(errorString);
      }
      setErrorStyle(true);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 300); // slight delay ensures keyboard opens reliably
    return () => clearTimeout(timer);
  }, []);

  const {textColorPrimary, playButtonGradient, textColorSecondary} =
    SkillzStyle.getOverriddenFallbackTheme();

  const renderNewUINextButton = (text: string) => {
    const isValid = verificationCode
      ? currentOTP.length === numberOfInputs
      : parsedNumber.length >= 2 && isValidNumber(fullNumber, country);

    return (
      <View style={newUIStyles.nextButtonContainer}>
        {/* {isResendCodeEnabled || verificationCode ? (
          <Text allowFontScaling={false} onPress={onSendPressed} style={newUIStyles.resendBtn}>
            {text}
          </Text>
        ) :  */}
        <PushButton
          colors={playButtonGradient}
          title={'Next'}
          onPress={verificationCode ? onSendOTPPressed : onSendPressed}
          cornerRadius={12}
          textStyle={newUIStyles?.btnText}
          style={{height: 56, opacity: !isValid && !isLoading ? 0.6 : 1}}
          isLoading={isLoading}
          disabled={!isValid || isLoading}
        />
      </View>
    );
  };

  const renderNewUIPhoneInput = () => (
    <>
      <Text allowFontScaling={false} style={newUIStyles.headerTitle}>
        {messageText?.header}{' '}
      </Text>
      <Text allowFontScaling={false} style={newUIStyles.headerDescription}>
        {messageText?.body}
      </Text>
      <View style={newUIStyles.phoneInputContainer}>
        <View
          style={[
            newUIStyles.unifiedPhoneInput,
            errorStyle && !verificationCode && !inputPhoneNumber ? newUIStyles.errorInput : null,
          ]}
        >
          {/* Left Section: Country Code */}
          <TouchableOpacity
            style={newUIStyles.countrySection}
            onPress={() => {
              setShowCountryList(true);
              trackEvent(events.countrySelect);
            }}
          >
            <View style={newUIStyles.flagContainer}>{getFlag(country, newUIStyles.flagImage)}</View>
            <Text allowFontScaling={false} style={newUIStyles.countryCodeText}>
              {countryValue}
            </Text>
            <SkillzSVG
              source={'polygon.svg'}
              style={newUIStyles.dropdownIcon}
              fill={textColorSecondary?.color}
            />
          </TouchableOpacity>

          {/* Vertical Separator */}
          <View style={newUIStyles.separator} />

          {/* Right Section: Phone Number Input */}
          <View style={newUIStyles.phoneInputSection}>
            <TextInput
              ref={inputRef}
              style={newUIStyles.phoneNumberInput}
              placeholder='Enter mobile number'
              placeholderTextColor={isDarkTheme ? textColorPrimary.color : textColorSecondary.color}
              value={phoneNumber}
              onChangeText={(text) => {
                setPhoneNumber(text);
                // Clear errors when user starts typing
                if (errorStyle) {
                  setErrorStyle(false);
                }
              }}
              keyboardType='phone-pad'
              autoComplete='tel'
              textContentType='telephoneNumber'
              allowFontScaling={false}
            />
          </View>
        </View>
      </View>
    </>
  );

  if (isNewUI) {
    return (
      <SafeAreaView style={[containerStyle, newUIStyles.container]}>
        {showBanner ? (
          <View
            style={[
              styles.banner,
              isNewUI && {
                position: 'absolute',
                top: 20,
                right: 0,
                left: 0,
                zIndex: 1000,
              },
            ]}
          >
            <BannerAnimated
              title={_m('Code Resent')}
              message={_m('This code expires in 10 minutes')}
              onClosePress={() => setShowBanner(false)}
            />
          </View>
        ) : null}

        <View>
          {verificationCode || inputPhoneNumber ? (
            <SMSCodeInput
              colorVariant={colorVariant}
              callInsteadEnabled={{
                value: callInsteadEnabled,
                set: setCallInsteadEnabled,
              }}
              trackEvent={trackEvent}
              callMeInsteadPress={() => {
                sendSMSVerification(
                  `${countryValue}${phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '')}`,
                  () => {},
                  (errorString) => {
                    handleSMSValidationError(errorString, phoneNumber, country, countryValue);
                  },
                  true
                );
                setCallInsteadEnabled(false);
              }}
              time={callTime}
              numberOfInputs={numberOfInputs}
              phoneNumber={phoneNumber}
              countryValue={countryValue}
              editPress={editPress}
              disableEdit={disableEdit}
              setErrorMessage={setErrorMessage}
              errorStyle={{errorStyle, setErrorStyle}}
              // codeValidation={codeValidation}
              codeValidation={_codeValidation}
              events={events}
              phoneInUseLogin={phoneNumberInUse ? true : false}
              isNewUI={isNewUI}
            />
          ) : (
            <>
              {renderNewUIPhoneInput()}
              {/* Show validation errors for phone input */}
              {!verificationCode && !inputPhoneNumber && errorStyle && errorMessage
                ? renderErrorMessage(errorMessage, false)
                : null}
            </>
          )}
          {(verificationCode || inputPhoneNumber) && errorStyle && errorMessage
            ? renderErrorMessage(errorMessage, verificationCode)
            : null}
        </View>
        <View
          {...(isNewUI && verificationCode && {style: newUIStyles.verificationCodeButtonContainer})}
        >
          {/* {showTosMessage && !verificationCode && !inputPhoneNumber ? renderTosMessage() : null} */}
          {renderSendCodeButton()}
        </View>

        <CountriesList
          visible={showCountryList}
          hide={() => setShowCountryList(false)}
          select={(value: string) => setCountry(value)}
          data={countryData}
          getFlag={getFlag}
        />
        {tosValues?.showTos ? renderTos() : null}
      </SafeAreaView>
    );
  }
  return (
    <SafeAreaView style={containerStyle}>
      {
        /* istanbul ignore next */
        showBanner ? (
          <View style={styles.banner}>
            <BannerAnimated
              title={_m('Code Resent')}
              message={_m('This code expires in 10 minutes')}
              onClosePress={() => setShowBanner(false)}
            />
          </View>
        ) : null
      }
      <View style={{marginTop: 20}}>
        {
          /* istanbul ignore next */
          verificationCode || inputPhoneNumber ? (
            <SMSCodeInput
              colorVariant={colorVariant}
              callInsteadEnabled={{
                value: callInsteadEnabled,
                set: setCallInsteadEnabled,
              }}
              trackEvent={trackEvent}
              callMeInsteadPress={() => {
                trackTelemetryEvent({
                  event: TelemetryEventName.SIGN_IN_OTP_SCREEN_CALLME_CLICKED,
                });
                sendSMSVerification(
                  `${countryValue}${phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '')}`,
                  () => {},
                  (errorString) => {
                    handleSMSValidationError(errorString, phoneNumber, country, countryValue);
                  },
                  true
                );
                setCallInsteadEnabled(false);
              }}
              time={callTime}
              numberOfInputs={numberOfInputs}
              phoneNumber={phoneNumber}
              countryValue={countryValue}
              editPress={editPress}
              disableEdit={disableEdit}
              setErrorMessage={setErrorMessage}
              errorStyle={{errorStyle, setErrorStyle}}
              codeValidation={codeValidation}
              events={events}
              phoneInUseLogin={phoneNumberInUse ? true : false}
            />
          ) : (
            <SMSPhoneInput
              colorVariant={colorVariant}
              getFlag={getFlag}
              showCountryList={{
                value: showCountryList,
                set: setShowCountryList,
              }}
              country={country}
              countryValue={countryValue}
              trackEvent={trackEvent}
              messageText={messageText}
              phoneNumber={{value: phoneNumber, set: setPhoneNumber}}
              errorStyle={errorStyle}
              errorMessage={errorMessage}
              formatNumber={formatNumber}
              events={events}
            />
          )
        }
        {(verificationCode || inputPhoneNumber) && errorStyle && errorMessage
          ? renderErrorMessage(errorMessage, verificationCode)
          : null}

        {showTosMessage && !verificationCode && !inputPhoneNumber ? renderTosMessage() : null}
        {renderSendCodeButton()}
      </View>
      <CountriesList
        visible={showCountryList}
        hide={() => setShowCountryList(false)}
        select={(value: string) => setCountry(value)}
        data={countryData}
        getFlag={getFlag}
      />
      {tosValues?.showTos ? renderTos() : null}
    </SafeAreaView>
  );
};
